import type { Venda, VendaItem } from "@/lib/api";

export type VendasFiscalExportFormat = "xml" | "excel";

type PeriodoFiscal = {
  inicio: string;
  fim: string;
};

type DateTimeParts = {
  dataIso: string;
  dataBr: string;
  hora: string;
};

const MISSING = "—";

const PAGAMENTO_LABELS: Array<[string, string]> = [
  ["pix", "Pix"],
  ["dinheiro", "Dinheiro"],
  ["credito", "Crédito"],
  ["debito", "Débito"],
];

function isMissingValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function numeroSeguro(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function moneyDot(value: unknown): string {
  return numeroSeguro(value).toFixed(2);
}

function moneyDotOrMissing(value: unknown): string {
  if (isMissingValue(value)) return MISSING;
  return moneyDot(value);
}

function moneyBR(value: unknown): string {
  return numeroSeguro(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function moneyBROrMissing(value: unknown): string {
  if (isMissingValue(value)) return MISSING;
  return moneyBR(value);
}

function quantidadeText(value: unknown): string {
  if (isMissingValue(value)) return MISSING;
  const n = numeroSeguro(value);
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 1,
    maximumFractionDigits: 3,
  });
}

function quantidadeXml(value: unknown): string {
  if (isMissingValue(value)) return MISSING;
  const n = numeroSeguro(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function splitVendaDateTime(value: unknown): DateTimeParts {
  const raw = String(value ?? "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) {
    return { dataIso: MISSING, dataBr: MISSING, hora: MISSING };
  }
  const [, y, mo, d, h, mi] = m;
  return {
    dataIso: `${y}-${mo}-${d}`,
    dataBr: `${d}/${mo}/${y}`,
    hora: `${h}:${mi}`,
  };
}

function escapeXml(value: unknown): string {
  return String(value ?? "")
    .replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFD]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function textoOuMissing(value: unknown): string {
  const s = String(value ?? "").trim();
  return s !== "" ? s : MISSING;
}

function pagamentoTexto(venda: Venda): string {
  const pagamento = venda.pagamento ?? {};
  const partes = PAGAMENTO_LABELS.flatMap(([key, label]) => {
    const valor = numeroSeguro((pagamento as Record<string, unknown>)[key]);
    return valor > 0.005 ? [label] : [];
  });
  return partes.length > 0 ? partes.join(" | ") : MISSING;
}

function itensVenda(venda: Venda): VendaItem[] {
  return Array.isArray(venda.itens) ? venda.itens : [];
}

export function nomeArquivoVendasFiscal(periodo: PeriodoFiscal, formato: VendasFiscalExportFormat): string {
  return `vendas_${periodo.inicio}_${periodo.fim}.${formato === "xml" ? "xml" : "xlsx"}`;
}

export function buildVendasFiscalXml(vendas: Venda[], periodo: PeriodoFiscal): string {
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    "<exportacao>",
    "  <periodo>",
    `    <data_inicial>${escapeXml(periodo.inicio)}</data_inicial>`,
    `    <data_final>${escapeXml(periodo.fim)}</data_final>`,
    "  </periodo>",
    "  <vendas>",
  ];

  for (const venda of vendas) {
    const dt = splitVendaDateTime(venda.createdAt);
    lines.push(`    <venda id="${escapeXml(venda.id)}">`);
    lines.push(`      <data_emissao>${escapeXml(dt.dataBr)}</data_emissao>`);
    lines.push(`      <hora_emissao>${escapeXml(dt.hora)}</hora_emissao>`);
    lines.push(`      <chave_sefaz>${escapeXml(textoOuMissing(venda.nfce_chave))}</chave_sefaz>`);
    lines.push(`      <cpf_cnpj>${escapeXml(textoOuMissing(venda.nfce_documento))}</cpf_cnpj>`);
    lines.push(`      <valor_total>${escapeXml(moneyDotOrMissing(venda.total))}</valor_total>`);
    lines.push(`      <forma_pagamento>${escapeXml(pagamentoTexto(venda))}</forma_pagamento>`);
    lines.push("      <itens>");
    for (const item of itensVenda(venda)) {
      lines.push("        <item>");
      lines.push(`          <nome>${escapeXml(textoOuMissing(item.nome_produto))}</nome>`);
      lines.push(`          <quantidade>${escapeXml(quantidadeXml(item.quantidade))}</quantidade>`);
      lines.push(`          <valor_unitario>${escapeXml(moneyDotOrMissing(item.preco_unitario))}</valor_unitario>`);
      lines.push(`          <valor_total>${escapeXml(moneyDotOrMissing(item.subtotal))}</valor_total>`);
      lines.push("        </item>");
    }
    lines.push("      </itens>");
    lines.push("    </venda>");
  }

  lines.push("  </vendas>", "</exportacao>");
  return lines.join("\n");
}

function cellRef(col: number, row: number): string {
  let n = col + 1;
  let label = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return `${label}${row}`;
}

function inlineStringCell(ref: string, value: unknown, style = 0): string {
  return `<c r="${ref}" t="inlineStr" s="${style}"><is><t>${escapeXml(value)}</t></is></c>`;
}

function numberCell(ref: string, value: unknown, style = 2): string {
  return `<c r="${ref}" s="${style}"><v>${moneyDot(value)}</v></c>`;
}

type XlsxColumnLimit = {
  min: number;
  max: number;
  padding?: number;
};

type XlsxDataRow = {
  values: unknown[];
  rawTotal: unknown;
  totalMissing: boolean;
};

const XLSX_COLUMN_LIMITS: XlsxColumnLimit[] = [
  { min: 10, max: 14 },
  { min: 12, max: 14 },
  { min: 8, max: 10 },
  { min: 12, max: 46 },
  { min: 14, max: 20 },
  { min: 16, max: 20 },
  { min: 16, max: 24 },
  { min: 16, max: 42 },
  { min: 12, max: 22 },
  { min: 14, max: 24 },
  { min: 18, max: 28 },
];

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function visibleTextLength(value: unknown): number {
  return String(value ?? "")
    .split(/\r?\n/)
    .reduce((max, part) => Math.max(max, part.length), 0);
}

function xlsxColumnsXml(headers: string[], dataRows: XlsxDataRow[]): string {
  const cols = headers.map((header, idx) => {
    const limit = XLSX_COLUMN_LIMITS[idx] ?? { min: 12, max: 32 };
    const contentWidth = dataRows.reduce(
      (max, row) => Math.max(max, visibleTextLength(row.values[idx])),
      visibleTextLength(header),
    );
    const width = clampNumber(contentWidth + (limit.padding ?? 2), limit.min, limit.max);
    return `<col min="${idx + 1}" max="${idx + 1}" width="${width.toFixed(1)}" customWidth="1"/>`;
  });
  return `<cols>${cols.join("")}</cols>`;
}

function xlsxSheetXml(vendas: Venda[]): string {
  const headers = [
    "ID da venda",
    "Data da emissão",
    "Hora da emissão",
    "Chave SEFAZ",
    "CPF/CNPJ cliente",
    "Valor total da nota",
    "Forma de pagamento",
    "Produtos",
    "Quantidades",
    "Valores unitários",
    "Valores totais dos itens",
  ];
  const dataRows: XlsxDataRow[] = vendas.map((venda) => {
    const dt = splitVendaDateTime(venda.createdAt);
    const itens = itensVenda(venda);
    const produtos = itens.length > 0 ? itens.map((item) => textoOuMissing(item.nome_produto)).join(" | ") : MISSING;
    const quantidades = itens.length > 0 ? itens.map((item) => quantidadeText(item.quantidade)).join(" | ") : MISSING;
    const unitarios = itens.length > 0 ? itens.map((item) => moneyBROrMissing(item.preco_unitario)).join(" | ") : MISSING;
    const totais = itens.length > 0 ? itens.map((item) => moneyBROrMissing(item.subtotal)).join(" | ") : MISSING;
    const totalMissing = isMissingValue(venda.total);

    return {
      values: [
        venda.id,
        dt.dataBr,
        dt.hora,
        textoOuMissing(venda.nfce_chave),
        textoOuMissing(venda.nfce_documento),
        totalMissing ? MISSING : `R$ ${moneyBR(venda.total)}`,
        pagamentoTexto(venda),
        produtos,
        quantidades,
        unitarios,
        totais,
      ],
      rawTotal: venda.total,
      totalMissing,
    };
  });

  const rows: string[] = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    xlsxColumnsXml(headers, dataRows),
    "<sheetData>",
  ];

  rows.push(`<row r="1">${headers.map((h, idx) => inlineStringCell(cellRef(idx, 1), h, 1)).join("")}</row>`);

  dataRows.forEach((row, idx) => {
    const rowNumber = idx + 2;
    const values = row.values;
    const cells = [
      inlineStringCell(cellRef(0, rowNumber), values[0]),
      inlineStringCell(cellRef(1, rowNumber), values[1]),
      inlineStringCell(cellRef(2, rowNumber), values[2]),
      inlineStringCell(cellRef(3, rowNumber), values[3]),
      inlineStringCell(cellRef(4, rowNumber), values[4]),
      row.totalMissing
        ? inlineStringCell(cellRef(5, rowNumber), MISSING)
        : numberCell(cellRef(5, rowNumber), row.rawTotal),
      inlineStringCell(cellRef(6, rowNumber), values[6]),
      inlineStringCell(cellRef(7, rowNumber), values[7]),
      inlineStringCell(cellRef(8, rowNumber), values[8]),
      inlineStringCell(cellRef(9, rowNumber), values[9]),
      inlineStringCell(cellRef(10, rowNumber), values[10]),
    ];
    rows.push(`<row r="${rowNumber}">${cells.join("")}</row>`);
  });

  rows.push("</sheetData>", "</worksheet>");
  return rows.join("");
}

function xlsxStylesXml(): string {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    '<numFmts count="1"><numFmt numFmtId="164" formatCode="R$ #,##0.00"/></numFmts>',
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>',
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>',
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>',
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
    '<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf></cellXfs>',
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>',
    "</styleSheet>",
  ].join("");
}

function crc32(data: Uint8Array): number {
  const table = crc32Table();
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = (crc >>> 8) ^ table[(crc ^ byte) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

let crcTableCache: Uint32Array | null = null;

function crc32Table(): Uint32Array {
  if (crcTableCache) return crcTableCache;
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  crcTableCache = table;
  return table;
}

function concatUint8(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function u16(value: number): Uint8Array {
  const out = new Uint8Array(2);
  new DataView(out.buffer).setUint16(0, value, true);
  return out;
}

function u32(value: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, value >>> 0, true);
  return out;
}

function zipDateParts(date: Date): { time: number; date: number } {
  const year = Math.max(1980, date.getFullYear());
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

function zipStore(files: Array<{ path: string; content: string }>): Uint8Array {
  const encoder = new TextEncoder();
  const now = zipDateParts(new Date());
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.path);
    const dataBytes = encoder.encode(file.content);
    const crc = crc32(dataBytes);
    const local = concatUint8([
      u32(0x04034b50),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(now.time),
      u16(now.date),
      u32(crc),
      u32(dataBytes.length),
      u32(dataBytes.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      dataBytes,
    ]);
    locals.push(local);

    centrals.push(concatUint8([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(now.time),
      u16(now.date),
      u32(crc),
      u32(dataBytes.length),
      u32(dataBytes.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]));
    offset += local.length;
  }

  const centralOffset = offset;
  const central = concatUint8(centrals);
  const end = concatUint8([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(centralOffset),
    u16(0),
  ]);

  return concatUint8([...locals, central, end]);
}

export function buildVendasFiscalXlsxBlob(vendas: Venda[]): Blob {
  const files = [
    {
      path: "[Content_Types].xml",
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>',
    },
    {
      path: "_rels/.rels",
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    },
    {
      path: "xl/workbook.xml",
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Vendas" sheetId="1" r:id="rId1"/></sheets></workbook>',
    },
    {
      path: "xl/_rels/workbook.xml.rels",
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>',
    },
    { path: "xl/styles.xml", content: xlsxStylesXml() },
    { path: "xl/worksheets/sheet1.xml", content: xlsxSheetXml(vendas) },
  ];
  const zip = zipStore(files);
  const buffer = new ArrayBuffer(zip.byteLength);
  new Uint8Array(buffer).set(zip);
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function buildVendasFiscalXmlBlob(vendas: Venda[], periodo: PeriodoFiscal): Blob {
  return new Blob([buildVendasFiscalXml(vendas, periodo)], {
    type: "application/xml;charset=utf-8",
  });
}

export function baixarBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
